import { utils, writeFile } from "xlsx";
import { packageStatus } from "./packageStatus";
import { formatRut } from "./formatter";
import { orderTypeLabel } from "./orderTypeLabel";
import i18n from "../i18n/index.js";

const formatDateTimeCLNoComma = (dateValue) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";

  // En "es-CL" suele venir como "21-12-2025, 00:29:55". Se requiere sin coma.
  return date
    .toLocaleString("es-CL", { timeZone: "America/Santiago", hour12: false })
    .replace(/,\s*/, " ");
};

export const downloadExcel = async ({ dataToExcel, fileName }) => {
  if (!Array.isArray(dataToExcel) || dataToExcel.length === 0) {
    console.warn('No hay datos para descargar.');
    return;
  }
  
  try {
    const data = dataToExcel;
    let filename = fileName;
    let wb = utils.book_new();
    let ws_name = i18n.t("hoja 1");
    let ws_data = [
      [
        "#",
        i18n.t("FECHA DE CREACIÓN DEL ENVÍO"),
        i18n.t("COMENTARIO DEL PAQUETE"),
        i18n.t("NÚMERO DE SEGUIMIENTO"),
        i18n.t("NOMBRE DEL DESTINATARIO"),
        i18n.t("NÚMERO DEL DESTINATARIO"),
        i18n.t("ESTADO DEL PAQUETE"),
        i18n.t("FECHA ÚLTIMA ACTUALIZACIÓN"),
        i18n.t("SOBREDIMENSIONADO"),
        i18n.t("FECHA DE ENTREGA"),
        i18n.t("NÚMERO DE VENTA"),
        i18n.t("NOMBRE DEL REPARTIDOR"),
        i18n.t("DIRECCIÓN DE ORIGEN"),
        i18n.t("NOMBRE DEL VENDEDOR"),
        i18n.t("NOMBRE DEL NEGOCIO"),
        i18n.t("NOMBRE DEL RECEPTOR"),
        i18n.t("RUT DEL RECEPTOR"),
        i18n.t("DIRECCIÓN DE DESTINO"),
        i18n.t("COMUNA DE DESTINO"),
        i18n.t("COMENTARIO DEL ENVÍO"),
      ],
    ];
    data?.forEach((item, index) => {
      const overSizedReason =
        item?.overSizedEvidence?.reason !== null &&
        item?.overSizedEvidence?.reason !== undefined
          ? String(item.overSizedEvidence.reason)
          : "";

      ws_data.push([
        index + 1,
        formatDateTimeCLNoComma(item?.createdAt) || null,
        item?.comments,
        item?.followingNumber,
        item?.receiver_name,
        item?.receiver_phone,
        packageStatus[item?.status],
        item?.updatedAt
          ? formatDateTimeCLNoComma(item.updatedAt)
          : item?.packageStatusHistory?.find((h) => h.status === 4)?.updatedAt
          ? formatDateTimeCLNoComma(
              item.packageStatusHistory.find((h) => h.status === 4).updatedAt,
            )
          : "",
        overSizedReason,
        formatDateTimeCLNoComma(item?.deliveryDate),
        item?.order_id,
        `${item?.assignTo?.name || ""} ${item?.assignTo?.lastName || ""}`,
        `${item?.senderInfo?.senderAddress}, ${item?.senderInfo?.senderMunicipality}`,
        `${item?.senderUser?.senderName || ""} ${item?.senderUser?.senderLastName || ""}`,
        item?.senderInfo?.businessName,
        `${item?.realReceiver?.name || ""} ${item?.realReceiver?.lastName || ""}`,
        formatRut(item?.realReceiver?.rut) || "",
        item?.shipping_address?.address_line,
        item?.shipping_address?.municipality?.name || "",
        item?.shipping_address?.comment || item?.comments || "",
      ]);
    });
    let ws = utils.aoa_to_sheet(ws_data);
    var col = [
      { wpx: 50 },
      { wpx: 200 },
      { wpx: 200 },
      { wpx: 200 },
      { wpx: 200 },
      { wpx: 200 },
      { wpx: 200 },
      { wpx: 200 },
      { wpx: 200 },
      { wpx: 200 },
      { wpx: 200 },
      { wpx: 200 },
      { wpx: 200 },
      { wpx: 200 },
      { wpx: 200 },
      { wpx: 200 },
      { wpx: 200 },
      { wpx: 200 },
      { wpx: 200 },
    ];
    ws["!cols"] = col;
    utils.book_append_sheet(wb, ws, ws_name);
    writeFile(wb, filename + ".xlsx");
  } catch (error) {
    console.error(error);
  }
};

export const downloadHoldingPackagesExcel = async ({ dataToExcel, fileName }) => {
  if (!Array.isArray(dataToExcel) || dataToExcel.length === 0) {
    console.warn("No hay datos para descargar.");
    return;
  }

  try {
    const data = dataToExcel;
    let filename = fileName;
    let wb = utils.book_new();
    let ws_name = i18n.t("Reporte Holding");
    let ws_data = [
      [
        "#",
        i18n.t("NÚMERO DE SEGUIMIENTO"),
        i18n.t("FECHA DE CREACIÓN"),
        i18n.t("ESTADO DEL PAQUETE"),
        i18n.t("NOMBRE DEL NEGOCIO"),
        i18n.t("FECHA DE RECOLECCIÓN"),
        i18n.t("FECHA DE ENTREGA"),
      ],
    ];
    const statusMap = {
      0: "No recolectado",
      1: "Recolectado",
      2: "En bodega",
      3: "En ruta",
      30: "En ruta a domicilio del comprador",
      4: "Entregado",
      5: "Cancelado",
      6: "Dirección incorrecta o incompleta",
      7: "Sin moradores",
      100: "Etiqueta impresa",
    };

    data?.forEach((item, index) => {
      ws_data.push([
        index + 1,
        item?.followingNumber || "",
        item?.createdAt
          ? new Date(item.createdAt).toLocaleString("es-CL", {
              timeZone: "America/Santiago",
              hour12: false,
            })
          : "",
        i18n.t(statusMap[item?.status] || "Desconocido"),
        item?.senderBusinessName || "",
        item?.collectedAt
          ? new Date(item.collectedAt).toLocaleString("es-CL", {
              timeZone: "America/Santiago",
              hour12: false,
            })
          : "",
        item?.deliveredAt
          ? new Date(item.deliveredAt).toLocaleString("es-CL", {
              timeZone: "America/Santiago",
              hour12: false,
            })
          : "",
      ]);
    });

    let ws = utils.aoa_to_sheet(ws_data);

    // Ajuste de columnas
    ws["!cols"] = [
      { wpx: 50 },  // #
      { wpx: 150 }, // NÚMERO DE SEGUIMIENTO
      { wpx: 200 }, // FECHA DE CREACIÓN
      { wpx: 150 }, // ESTADO DEL PAQUETE
      { wpx: 200 }, // NOMBRE DEL NEGOCIO
      { wpx: 200 }, // FECHA DE RECOLECCIÓN
      { wpx: 200 }, // FECHA DE ENTREGA
    ];

    utils.book_append_sheet(wb, ws, ws_name);
    writeFile(wb, filename + ".xlsx");
  } catch (error) {
    console.error("Error al generar el archivo Excel:", error);
  }
};

export const downloadDeliveredPackagesExcel = async ({ dataToExcel, fileName }) => {
  if (!Array.isArray(dataToExcel) || dataToExcel.length === 0) {
    console.warn("No hay datos para descargar.");
    return;
  }

  try {
    const data = dataToExcel;
    let filename = fileName;
    let wb = utils.book_new();
    let ws_name = i18n.t("Reporte entregas");
    let ws_data = [
      [
        i18n.t("NÚMERO DE SEGUIMIENTO"),
        i18n.t("DIRECCIÓN DE DESTINO"),
        i18n.t("COMUNA DE DESTINO"),
        i18n.t("TIPO"),
        i18n.t("NOMBRE DEL NEGOCIO"),
        i18n.t("IDENTIFICADOR DEL NEGOCIO"),
        i18n.t("FECHA DE ENTREGA"),
        i18n.t("FECHA DE RETIRO"),
        i18n.t("NOMBRE DEL REPARTIDOR"),
        i18n.t("EMAIL REPARTIDOR"),
        i18n.t("MOTIVO SOBREDIMENSIONADO")
      ],
    ];

    data?.forEach((item, index) => {
      ws_data.push([
        item?.followingNumber || "",
        item?.shipping_address?.address_line || "",
        item?.shipping_address?.municipality?.name || "",
        orderTypeLabel(item?.type) || "",
        item?.companyBusinessIdentifier || "",
        item?.senderInfo?.businessName || "",
        item?.updatedAt
          ? new Date(item.updatedAt).toLocaleString("es-CL", {
              timeZone: "America/Santiago",
              hour12: false,
            })
          : "",
        item?.collectDate
          ? new Date(item.collectDate).toLocaleString("es-CL", {
              timeZone: "America/Santiago",
              hour12: false,
            })
          : "",
        item?.userName + " " + item?.userLastName,
        item?.userEmail,
        item?.overSizedEvidence?.reason || null,
      ]);
    });

    let ws = utils.aoa_to_sheet(ws_data);

    ws["!cols"] = [
      { wpx: 150 }, // NÚMERO DE SEGUIMIENTO
      { wpx: 200 }, // DIRECCIÓN DE DESTINO
      { wpx: 200 }, // COMUNA DE DESTINO
      { wpx: 150 }, // TIPO
      { wpx: 200 }, // NOMBRE DEL NEGOCIO
      { wpx: 200 }, // IDENTIFICADOR DEL NEGOCIO
      { wpx: 200 }, // FECHA DE ACTUALIZACIÓN
      { wpx: 200 }, // FECHA DE RETIRO
      { wpx: 200 }, // NOMBRE DEL USUARIO
      { wpx: 200 }, // EMAIL DEL USUARIO
      { wpx: 200 }, // MOTIVO SOBREDIMENSIONADO
    ];

    utils.book_append_sheet(wb, ws, ws_name);
    writeFile(wb, filename + ".xlsx");
  } catch (error) {
    console.error("Error al generar el archivo Excel:", error);
  }
};